import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import { ApplicationType, MediaType } from '../types/translation-types';
import {
  MediaItem,
  UploadSoundRequest,
  DEFAULT_SOUNDS_PAGE_SIZE,
  CopySoundsResult,
} from '../types/sound-management-types';

interface SoundManagementStore {
  // Data
  sounds: MediaItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;

  // Filters
  applicationType: ApplicationType;
  languageFilter: string | null;

  // UI States
  isLoading: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isCopyingSounds: boolean;
  error: string | null;
  uploadError: string | null;
  copySoundsError: string | null;

  // Dialog state
  uploadDialogOpen: boolean;
  deleteDialogOpen: boolean;
  soundToDelete: MediaItem | null;
  copySoundsDialogOpen: boolean;

  // Audio playback
  playingMediaId: number | null;

  // Actions
  fetchSounds: () => Promise<void>;
  uploadSound: (request: UploadSoundRequest, skipRefresh?: boolean) => Promise<MediaItem>;
  deleteSound: (mediaId: number) => Promise<boolean>;
  copySoundsFromParent: (
    newLanguageCode: string,
    baseLanguageCode: string
  ) => Promise<CopySoundsResult>;
  setApplicationType: (appType: ApplicationType) => void;
  setLanguageFilter: (languageCode: string | null) => void;
  setCurrentPage: (page: number) => void;
  openUploadDialog: () => void;
  closeUploadDialog: () => void;
  openDeleteDialog: (sound: MediaItem) => void;
  closeDeleteDialog: () => void;
  openCopySoundsDialog: () => void;
  closeCopySoundsDialog: () => void;
  setPlayingMediaId: (mediaId: number | null) => void;
  clearUploadError: () => void;
  reset: () => void;
}

export const useSoundManagementStore = create<SoundManagementStore>((set, get) => ({
  // Initial state
  sounds: [],
  totalCount: 0,
  currentPage: 0,
  pageSize: DEFAULT_SOUNDS_PAGE_SIZE,
  applicationType: ApplicationType.EnergyAndroidKiosk, // Default to Kiosk
  languageFilter: null,
  isLoading: false,
  isUploading: false,
  isDeleting: false,
  isCopyingSounds: false,
  error: null,
  uploadError: null,
  copySoundsError: null,
  uploadDialogOpen: false,
  deleteDialogOpen: false,
  soundToDelete: null,
  copySoundsDialogOpen: false,
  playingMediaId: null,

  // Actions
  fetchSounds: async () => {
    const { applicationType, languageFilter, currentPage, pageSize } = get();

    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getSoundsUseCase.execute({
        applicationType,
        mediaType: MediaType.Sound,
        languageCode: languageFilter || undefined,
        skip: currentPage * pageSize,
        take: pageSize,
      });

      if (response.isSuccess) {
        set({
          sounds: response.rows,
          totalCount: response.count,
          isLoading: false,
        });
      } else {
        set({
          error: response.errorMessage || 'Failed to load sounds',
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load sounds',
        isLoading: false,
      });
    }
  },

  uploadSound: async (request: UploadSoundRequest, skipRefresh = false) => {
    set({ isUploading: true, uploadError: null });
    try {
      const services = servicesContainer.getServices();
      const media = await services.uploadSoundUseCase.execute(request);

      // Only refresh if not skipped (for batch uploads, caller handles refresh)
      if (!skipRefresh) {
        await get().fetchSounds();
        set({ isUploading: false, uploadDialogOpen: false });
      } else {
        set({ isUploading: false });
      }
      return media;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upload sound';
      set({ uploadError: message, isUploading: false });
      throw error;
    }
  },

  deleteSound: async (mediaId: number) => {
    set({ isDeleting: true });
    try {
      const services = servicesContainer.getServices();
      const response = await services.deleteSoundUseCase.execute(mediaId);

      if (response.isSuccess) {
        // Refresh the list
        await get().fetchSounds();
        set({
          isDeleting: false,
          deleteDialogOpen: false,
          soundToDelete: null,
        });
        return true;
      } else {
        set({
          isDeleting: false,
          error: response.errorMessage || 'Failed to delete sound',
        });
        return false;
      }
    } catch (error) {
      set({
        isDeleting: false,
        error: error instanceof Error ? error.message : 'Failed to delete sound',
      });
      return false;
    }
  },

  copySoundsFromParent: async (newLanguageCode: string, baseLanguageCode: string) => {
    set({ isCopyingSounds: true, copySoundsError: null });
    try {
      const services = servicesContainer.getServices();
      const result = await services.copySoundsUseCase.execute(newLanguageCode, baseLanguageCode);
      set({ isCopyingSounds: false });
      await get().fetchSounds();
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to copy sound files';
      set({ isCopyingSounds: false, copySoundsError: message });
      throw error;
    }
  },

  setApplicationType: (appType: ApplicationType) => {
    set({
      applicationType: appType,
      currentPage: 0,
      sounds: [],
      totalCount: 0,
    });
  },

  setLanguageFilter: (languageCode: string | null) => {
    set({
      languageFilter: languageCode,
      currentPage: 0,
    });
  },

  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },

  openUploadDialog: () => {
    set({ uploadDialogOpen: true, uploadError: null });
  },

  closeUploadDialog: () => {
    set({ uploadDialogOpen: false, uploadError: null });
  },

  openDeleteDialog: (sound: MediaItem) => {
    set({
      deleteDialogOpen: true,
      soundToDelete: sound,
    });
  },

  closeDeleteDialog: () => {
    set({
      deleteDialogOpen: false,
      soundToDelete: null,
    });
  },

  openCopySoundsDialog: () => {
    set({ copySoundsDialogOpen: true, copySoundsError: null });
  },

  closeCopySoundsDialog: () => {
    set({ copySoundsDialogOpen: false, copySoundsError: null });
  },

  setPlayingMediaId: (mediaId: number | null) => {
    set({ playingMediaId: mediaId });
  },

  clearUploadError: () => {
    set({ uploadError: null });
  },

  reset: () => {
    set({
      sounds: [],
      totalCount: 0,
      currentPage: 0,
      pageSize: DEFAULT_SOUNDS_PAGE_SIZE,
      applicationType: ApplicationType.EnergyAndroidKiosk,
      languageFilter: null,
      isLoading: false,
      isUploading: false,
      isDeleting: false,
      isCopyingSounds: false,
      error: null,
      uploadError: null,
      copySoundsError: null,
      uploadDialogOpen: false,
      deleteDialogOpen: false,
      soundToDelete: null,
      copySoundsDialogOpen: false,
      playingMediaId: null,
    });
  },
}));
